<template>
  <div class="dashboard-container">
    <el-row :gutter="20" class="dashboard-stats">
      <el-col :span="6">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-item">
            <div class="stat-icon bg-blue">
              <el-icon><ChatLineRound /></el-icon>
            </div>
            <div class="stat-info">
              <div class="stat-value">{{ dashboardData?.todayTokenUsage || 0 }}</div>
              <div class="stat-label">今日Token消耗</div>
            </div>
          </div>
        </el-card>
      </el-col>
      
      <el-col :span="6">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-item">
            <div class="stat-icon bg-green">
              <el-icon><User /></el-icon>
            </div>
            <div class="stat-info">
              <div class="stat-value">{{ dashboardData?.onlineUsers || 0 }}</div>
              <div class="stat-label">在线人数</div>
            </div>
          </div>
        </el-card>
      </el-col>
      
      <el-col :span="6">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-item">
            <div class="stat-icon bg-orange">
              <el-icon><UserFilled /></el-icon>
            </div>
            <div class="stat-info">
              <div class="stat-value">{{ dashboardData?.newUsersToday || 0 }}</div>
              <div class="stat-label">今日新用户</div>
            </div>
          </div>
        </el-card>
      </el-col>
      
      <el-col :span="6">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-item">
            <div class="stat-icon bg-purple">
              <el-icon><Warning /></el-icon>
            </div>
            <div class="stat-info">
              <div class="stat-value">{{ pendingTotal }}</div>
              <div class="stat-label">待处理事项</div>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="20" class="dashboard-overview">
      <el-col :span="12">
        <el-card>
          <template #header>
            <div class="card-header">
              <span>Token消耗趋势</span>
              <el-radio-group v-model="tokenPeriod" size="small" @change="onTokenPeriodChange">
                <el-radio-button value="daily">日</el-radio-button>
                <el-radio-button value="weekly">周</el-radio-button>
                <el-radio-button value="monthly">月</el-radio-button>
              </el-radio-group>
            </div>
          </template>
          <div ref="tokenChartRef" class="chart-container"></div>
        </el-card>
      </el-col>
      
      <el-col :span="12">
        <el-card>
          <template #header>
            <div class="card-header">
              <span>新用户增长趋势</span>
              <el-radio-group v-model="userPeriod" size="small" @change="onUserPeriodChange">
                <el-radio-button value="daily">日</el-radio-button>
                <el-radio-button value="weekly">周</el-radio-button>
                <el-radio-button value="monthly">月</el-radio-button>
              </el-radio-group>
            </div>
          </template>
          <div ref="userChartRef" class="chart-container"></div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="20" class="dashboard-additional-stats">
      <el-col :span="8">
        <el-card>
          <div class="additional-stat">
            <div class="stat-title">总用户数</div>
            <div class="stat-count">{{ dashboardData?.totalUsers || 0 }}</div>
          </div>
        </el-card>
      </el-col>
      
      <el-col :span="8">
        <el-card>
          <div class="additional-stat">
            <div class="stat-title">总帖子数</div>
            <div class="stat-count">{{ dashboardData?.totalPosts || 0 }}</div>
          </div>
        </el-card>
      </el-col>
      
      <el-col :span="8">
        <el-card>
          <div class="additional-stat">
            <div class="stat-title">总评论数</div>
            <div class="stat-count">{{ dashboardData?.totalComments || 0 }}</div>
          </div>
        </el-card>
      </el-col>
    </el-row>
    
    <el-row :gutter="20" class="dashboard-pending-stats">
      <el-col :span="8">
        <el-card>
          <div class="pending-stat">
            <div class="stat-title">待审核帖子</div>
            <div class="stat-count pending-count">{{ dashboardData?.pendingPostCount || 0 }}</div>
          </div>
        </el-card>
      </el-col>
      
      <el-col :span="8">
        <el-card>
          <div class="pending-stat">
            <div class="stat-title">待审核评论</div>
            <div class="stat-count pending-count">{{ dashboardData?.pendingCommentCount || 0 }}</div>
          </div>
        </el-card>
      </el-col>
      
      <el-col :span="8">
        <el-card>
          <div class="pending-stat">
            <div class="stat-title">待处理举报</div>
            <div class="stat-count pending-count">{{ dashboardData?.pendingReportCount || 0 }}</div>
          </div>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, nextTick, computed, onUnmounted } from 'vue';
import { getAdminDashboardStats, DashboardVO } from '@/admin/api/dashboard';
import { ElIcon, ElRow, ElCol, ElCard, ElRadioGroup, ElRadioButton } from 'element-plus';
import { 
  ChatLineRound, 
  User, 
  UserFilled, 
  Warning 
} from '@element-plus/icons-vue';
import * as echarts from 'echarts';

// 图表引用
const tokenChartRef = ref<HTMLDivElement>();
const userChartRef = ref<HTMLDivElement>();

// 图表实例
let tokenChart: echarts.ECharts | null = null;
let userChart: echarts.ECharts | null = null;

// 数据
const dashboardData = ref<DashboardVO | null>(null);
const loading = ref(true);

// 图表时间周期选择
const tokenPeriod = ref<'daily' | 'weekly' | 'monthly'>('daily');
const userPeriod = ref<'daily' | 'weekly' | 'monthly'>('daily');

// 计算待处理总数
const pendingTotal = computed(() => {
  if (!dashboardData.value) return 0;
  return (
    dashboardData.value.pendingPostCount +
    dashboardData.value.pendingCommentCount +
    dashboardData.value.pendingReportCount
  );
});

// 初始化图表
const initCharts = async () => {
  await nextTick();
  
  // 初始化ECharts图表
  if (tokenChartRef.value) {
    tokenChart = echarts.init(tokenChartRef.value);
    updateTokenChart();
  }

  if (userChartRef.value) {
    userChart = echarts.init(userChartRef.value);
    updateUserChart();
  }
};

// 更新Token图表
const updateTokenChart = () => {
  if (!tokenChart || !dashboardData.value) return;

  let trendData;
  let periodLabel;
  
  switch (tokenPeriod.value) {
    case 'daily':
      trendData = dashboardData.value.tokenTrendDaily;
      periodLabel = '最近30天';
      break;
    case 'weekly':
      trendData = dashboardData.value.tokenTrendWeekly;
      periodLabel = '最近12周';
      break;
    case 'monthly':
      trendData = dashboardData.value.tokenTrendMonthly;
      periodLabel = '最近12个月';
      break;
  }

  const option = {
    title: {
      text: `Token消耗趋势 - ${periodLabel}`,
      textStyle: { fontSize: 16 }
    },
    tooltip: { trigger: 'axis' },
    legend: { data: ['Token消耗'] },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: trendData.map(item => item.label)
    },
    yAxis: { type: 'value' },
    series: [{
      name: 'Token消耗',
      type: 'line',
      smooth: true,
      data: trendData.map(item => item.value),
      itemStyle: { color: '#409EFF' },
      areaStyle: { opacity: 0.3 }
    }]
  };

  tokenChart.setOption(option);
};

// 更新用户图表
const updateUserChart = () => {
  if (!userChart || !dashboardData.value) return;

  let trendData;
  let periodLabel;
  
  switch (userPeriod.value) {
    case 'daily':
      trendData = dashboardData.value.newUserTrendDaily;
      periodLabel = '最近30天';
      break;
    case 'weekly':
      trendData = dashboardData.value.newUserTrendWeekly;
      periodLabel = '最近12周';
      break;
    case 'monthly':
      trendData = dashboardData.value.newUserTrendMonthly;
      periodLabel = '最近12个月';
      break;
  }

  const option = {
    title: {
      text: `新用户增长趋势 - ${periodLabel}`,
      textStyle: { fontSize: 16 }
    },
    tooltip: { trigger: 'axis' },
    legend: { data: ['新用户'] },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: trendData.map(item => item.label)
    },
    yAxis: { type: 'value' },
    series: [{
      name: '新用户',
      type: 'line',
      smooth: true,
      data: trendData.map(item => item.value),
      itemStyle: { color: '#67C23A' },
      areaStyle: { opacity: 0.3 }
    }]
  };

  userChart.setOption(option);
};

// 切换Token图表时间周期
const onTokenPeriodChange = () => {
  updateTokenChart();
};

// 切换用户图表时间周期
const onUserPeriodChange = () => {
  updateUserChart();
};

// 加载数据
const loadDashboardData = async () => {
  try {
    loading.value = true;
    const response = await getAdminDashboardStats();
    dashboardData.value = response.data;
    initCharts();
  } catch (error) {
    console.error('Failed to load dashboard data:', error);
  } finally {
    loading.value = false;
  }
};

// 页面加载完成后获取数据
onMounted(() => {
  loadDashboardData();
});

// 当窗口大小改变时，调整图表尺寸
const handleResize = () => {
  if (tokenChart) tokenChart.resize();
  if (userChart) userChart.resize();
};

// 添加窗口大小改变事件监听器
window.addEventListener('resize', handleResize);

// 在组件卸载时销毁图表实例
onUnmounted(() => {
  if (tokenChart) {
    tokenChart.dispose();
    tokenChart = null;
  }
  if (userChart) {
    userChart.dispose();
    userChart = null;
  }
  window.removeEventListener('resize', handleResize);
});
</script>

<style scoped>
.dashboard-container {
  padding: 10px; /* 减少容器内边距 */
  background-color: #f5f7fa;
  height: 100%;
  width: 100%;
  overflow-y: auto;
  box-sizing: border-box;
}

.dashboard-stats {
  margin-bottom: 15px; /* 减少统计卡片区域的下边距 */
}

.stat-card {
  border-radius: 10px; /* 减少圆角 */
  border: none;
  box-shadow: 0 2px 6px rgba(0,0,0,.06); /* 减少阴影 */
  transition: transform 0.3s, box-shadow 0.3s;
  cursor: pointer;
}

.stat-card:hover {
  transform: translateY(-3px); /* 减少悬停效果 */
  box-shadow: 0 3px 10px rgba(0,0,0,.1);
}

.stat-item {
  padding: 12px; /* 减少统计项内边距 */
  display: flex;
  align-items: center;
}

.stat-icon {
  width: 50px; /* 减少图标尺寸 */
  height: 50px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px; /* 减少右边距 */
  color: white;
  font-size: 20px; /* 减少图标大小 */
}

.bg-blue {
  background: linear-gradient(135deg, #409eff, #337ecc);
}

.bg-green {
  background: linear-gradient(135deg, #67c23a, #529b2e);
}

.bg-orange {
  background: linear-gradient(135deg, #e6a23c, #c18b33);
}

.bg-purple {
  background: linear-gradient(135deg, #9013fe, #7410cb);
}

.stat-info {
  flex: 1;
  text-align: left;
}

.stat-value {
  font-size: 20px; /* 减小数值字体 */
  font-weight: bold;
  margin: 0 0 4px 0; /* 减少下边距 */
  color: #303133;
  line-height: 1.2;
}

.stat-label {
  font-size: 12px; /* 减小标签字体 */
  margin: 0;
  color: #909399;
  line-height: 1.4;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.chart-container {
  height: 250px; /* 减少图表高度 */
  width: 100%;
  overflow: hidden;
}

.dashboard-overview {
  margin-bottom: 15px; /* 减少概览区域下边距 */
}

.dashboard-additional-stats {
  margin-bottom: 15px; /* 减少附加统计区域下边距 */
}

.dashboard-pending-stats {
  margin-bottom: 15px; /* 减少待处理统计区域下边距 */
}

.additional-stat, .pending-stat {
  text-align: center;
}

.stat-title {
  font-size: 13px; /* 减小标题字体 */
  color: #909399;
  margin-bottom: 6px; /* 减少下边距 */
}

.stat-count {
  font-size: 24px; /* 减小计数字体 */
  font-weight: bold;
  color: #303133;
}

.pending-count {
  color: #f56c6c;
}

/* 调整响应式布局 */
@media (max-width: 768px) {
  .dashboard-container {
    padding: 8px; /* 在小屏幕上进一步减少内边距 */
  }
  
  .dashboard-stats, 
  .dashboard-overview, 
  .dashboard-additional-stats, 
  .dashboard-pending-stats {
    :deep(.el-col) {
      margin-bottom: 12px; /* 减少列之间的间距 */
    }
  }
  
  .chart-container {
    height: 220px; /* 在小屏幕上进一步减少图表高度 */
  }
  
  .stat-item {
    padding: 10px; /* 在小屏幕上减少统计项内边距 */
  }
}
</style>